import { googleLogIn } from '@/actions/auth/loginGoogle';
import Button from '@/components/core/Button';
import Icon from '@/components/core/Icon/Icon';
import Label from '@/components/core/Label/Label';
import Typography from '@/components/core/Typography';
import { useRouter } from '@/i18n/routing';
import { useGoogleLogin, useGoogleOAuth, useGoogleOneTapLogin } from '@react-oauth/google';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import styled from 'styled-components';
import { getUser } from '@/actions/account/getUser';
import { intersection } from 'lodash';
import { getQuickstartState } from '@/actions/account/getQuickstartState';
import { device } from '@/components/Media';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
    justify-content: center;

    @media ${device.laptopS} {
        width: 100%;
    }
`;

const GoogleButton = styled(Button)`
    height: auto;
    padding: 12px;

    @media ${device.laptopS} {
        width: 100%;
        justify-content: center;
    }
`;

const ErrorLabel = styled(Label)`
    background-color: ${({ theme }) => theme.colors.palette.red_100};
    padding: 12px;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
`;

const ErrorIcon = styled(Icon)`
    background-color: ${({ theme }) => theme.colors.palette.red_300};
    margin-top: 3px;
`;

const ErrorTypography = styled(Typography)`
    color: ${({ theme }) => theme.colors.palette.red_300};
`;

export const postLoginRedirect = (router, user) => {
    if (!user) {
        router.replace('/');
        return;
    }

    const firstStepsPermissions = ['api.topology.create', 'api.mass-actions.import', 'api.farms.update'];

    if (intersection(firstStepsPermissions, user.permissions).length < firstStepsPermissions.length) {
        router.replace('/');

        return;
    }

    getQuickstartState().then((quickStartState) => {
        if ([quickStartState?.has_devices, quickStartState?.has_cells, quickStartState?.imported_devices].some(val => val === false)) {
            router.replace('/settings/quickstart');
        } else {
            router.replace('/');
        }
    }).catch(() => {
        router.replace('/');
    });
};

const GoogleAuth = ({ variant = 'SignIn' }: {
    variant?: 'SignIn' | 'SignUp';
}) => {
    const t = useTranslations();
    const router = useRouter();
    const [error, setError] = useState('');
    const googleContext = useGoogleOAuth();

    const handleLogIn = (code, token) => {
        googleLogIn(code, token)
            .then(async () => {
                const user = await getUser();

                postLoginRedirect(router, user);
            })
            .catch(() => {
                setError(t('common.internalError'));
            });
    };

    useGoogleOneTapLogin({
        onSuccess: credentialResponse => {
            handleLogIn(null, credentialResponse.credential);
        },
        onError: () => {
            setError(t('auth.googleSignInServerError'));
        }
    });

    const login = useGoogleLogin({
        flow: 'auth-code',
        onSuccess: (credentialResponse) => {
            handleLogIn(credentialResponse.code, null);
        },
        onError: (errorResponse) => {
            switch (errorResponse.error) {
                case 'invalid_request':
                case 'invalid_scope':
                case 'unauthorized_client':
                    setError(t('auth.googleSignInUnknownError'));
                    break;
                case 'access_denied':
                    setError(t('auth.googleSignInClosed'));
                    break;
                default:
                    setError(t('auth.googleSignInServerError'));
            }
        },
        onNonOAuthError: (errorResponse) => {
            switch (errorResponse.type) {
                case 'popup_closed':
                    setError(t('auth.googleSignInClosed'));
                    break;
                case 'popup_failed_to_open':
                    setError(t('auth.googleSignInFailedToOpen'));
                    break;
                default:
                    setError(t('auth.googleSignInUnknownError'));
            }
        }
    });

    return (
        <Wrapper>
            <GoogleButton
              variant='stroke'
              startAdornment={<Icon name='google' withoutMask />}
              innerText={t(`auth.google${variant}`)}
              onClick={() => {
                  if (googleContext && !googleContext.scriptLoadedSuccessfully) {
                      setError(t('auth.googleSignInFailedToOpen'));
                  } else {
                      setError('');
                  }
                  login();
              }}
            />
            {
                error && (
                    <ErrorLabel size='xs' noChildrenWrap>
                        <ErrorIcon name='alert-triangle-light' noMask />
                        <ErrorTypography variant='uiType' size='xs' modifier='R'>
                            {error}
                        </ErrorTypography>
                    </ErrorLabel>
                )
            }
        </Wrapper>
    );
};

export default GoogleAuth;
